<template>
  <div v-loading="loading" class="policy-detail-page">
    <header class="policy-detail-page__header">
      <h1 class="policy-detail-page__title">{{ pageTitle }}</h1>
      <div v-if="detail" class="policy-detail-page__actions">
        <div
          v-if="showInvoiceNotificationCallout"
          class="policy-detail-invoice-callout"
          :class="{
            'policy-detail-invoice-callout--retry': showRetryPolicyIssueButton,
            'policy-detail-invoice-callout--resend': showResendNotificationButton,
          }"
          role="note"
        >
          <i class="bi bi-chevron-double-left policy-detail-invoice-callout__arrow" aria-hidden="true" />
          <span>{{ invoiceNotificationCalloutText }}</span>
        </div>
        <button
          v-if="canPrintPolicy"
          type="button"
          class="policy-detail-page__action-btn"
          :disabled="printLoading"
          @click="onPrintPolicy"
        >
          <i
            class="fa"
            :class="printLoading ? 'fa-spinner fa-spin' : 'fa-print'"
            aria-hidden="true"
          />
          {{ printLoading ? ($t("catalogInsertCreating") || "...") : $t("print") }}
        </button>
        <button
          v-if="showRetryPolicyIssueButton"
          type="button"
          class="policy-detail-page__action-btn policy-detail-page__action-btn--invoice"
          :disabled="retryingPolicyIssue"
          @click="onRetryPolicyIssue"
        >
          <i class="bi bi-arrow-repeat" aria-hidden="true" />
          {{ $t("policyDetailRetryPolicyIssue") }}
        </button>
        <button
          v-if="showResendNotificationButton"
          type="button"
          class="policy-detail-page__action-btn policy-detail-page__action-btn--invoice"
          :disabled="notificationSending || notificationChecking"
          @click="onResendNotification"
        >
          <i class="bi bi-send" aria-hidden="true" />
          {{ $t("policyDetailResendNotification") }}
        </button>
        <button
          v-if="showDeleteAgentRequest"
          type="button"
          class="policy-detail-page__action-btn policy-detail-page__action-btn--danger"
          :disabled="deleting"
          @click="onDeleteAgentRequest"
        >
          <i class="bi bi-trash3" aria-hidden="true" />
          {{ $t("btnDelete") }}
        </button>
      </div>
    </header>

    <p v-if="!loading && !detail" class="policy-detail-empty">
      {{ $t("policyDetailNotFound") || "Məlumat tapılmadı" }}
    </p>

    <template v-if="detail">
      <section class="policy-detail-section">
        <article class="hezabat-card policy-detail-contract">
          <div class="policy-detail-contract__grid">
            <div class="policy-detail-contract__col">
              <h3 class="policy-detail-block-title">{{ $t("general_info") }}</h3>
              <table class="policy-detail-table-info">
                <tbody>
                  <tr v-for="row in generalInfoRows" :key="'g-' + row.key">
                    <td class="policy-detail-table-info__label">{{ row.label }}</td>
                    <td class="policy-detail-table-info__value">
                      <i
                        v-if="row.check === true"
                        class="bi bi-check-lg policy-detail-check"
                      />
                      <span v-else>{{ row.value || "—" }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="showInsuredColumn" class="policy-detail-contract__col">
              <h3 class="policy-detail-block-title">{{ $t("colInsured") }}</h3>
              <table class="policy-detail-table-info">
                <tbody>
                  <tr v-for="row in insuredRows" :key="'i-' + row.key">
                    <td class="policy-detail-table-info__label">{{ row.label }}</td>
                    <td class="policy-detail-table-info__value">{{ row.value || "—" }}</td>
                  </tr>
                </tbody>
              </table>
              <h3 class="policy-detail-block-title policy-detail-block-title--spaced">
                {{ $t("headerBeneficiary") }}
              </h3>
              <table class="policy-detail-table-info">
                <tbody>
                  <tr v-for="row in beneficiaryRows" :key="'b-' + row.key">
                    <td class="policy-detail-table-info__label">{{ row.label }}</td>
                    <td class="policy-detail-table-info__value">{{ row.value || "—" }}</td>
                  </tr>
                </tbody>
              </table>
              <template v-if="showInvoiceSection">
                <h3 class="policy-detail-block-title policy-detail-block-title--spaced">
                  {{ $t("policyDetailInvoice") }}
                </h3>
                <table class="policy-detail-table-info">
                  <tbody>
                    <tr v-for="row in invoiceRows" :key="'inv-' + row.key">
                      <td class="policy-detail-table-info__label">{{ row.label }}</td>
                      <td class="policy-detail-table-info__value">
                        <a
                          v-if="row.isLink && row.value"
                          class="policy-detail-invoice-link"
                          :href="row.value"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ row.value }}
                        </a>
                        <span v-else-if="!row.isLink">{{ row.value || "—" }}</span>
                        <span v-else>—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </div>
          </div>
        </article>
      </section>

      <!-- Sığorta olunanlar (2-ci) -->
      <section
        v-if="showInsuredCardsSection"
        class="policy-detail-section"
        :class="{
          'policy-detail-section--last':
            detail.detailMode === 'agentRequest' ||
            (isProductDetail && !showProductProgramsSection && !showProductGroupsSection && !showProductExceptionsSection),
        }"
      >
        <h2 class="policy-detail-section__title">{{ $t("insuredPersons") }}</h2>
        <article class="hezabat-card">
          <el-table :data="detail.insuredCards || []" class="hezabat-table" stripe>
            <template #empty>
              <span class="policy-detail-table-empty">{{ $t("policyDetailTableEmpty") }}</span>
            </template>
            <el-table-column prop="cardFullNumber" :label="$t('cardNumber')" width="120" />
            <el-table-column prop="insuredName" :label="$t('colInsured')" min-width="220" />
            <el-table-column prop="groupName" :label="$t('colGroup')" width="120" />
            <el-table-column prop="cardStatusName" :label="$t('colStatus')" width="110" />
            <el-table-column prop="sexStr" :label="$t('colSex')" width="70" />
            <el-table-column prop="startDateStr" :label="$t('startDate')" width="110" />
            <el-table-column prop="endDateStr" :label="$t('columnEndDate_1')" width="110" />
            <el-table-column prop="dobStr" :label="$t('dob')" width="110" />
          </el-table>
        </article>
      </section>

      <!-- Proqramlar -->
      <section v-if="showProductProgramsSection" class="policy-detail-section">
        <h2 class="policy-detail-section__title">{{ $t("programs") }}</h2>
        <article class="hezabat-card">
          <el-table :data="detail.programs || []" class="hezabat-table" stripe>
            <template #empty>
              <span class="policy-detail-table-empty">{{ $t("policyDetailTableEmpty") }}</span>
            </template>
            <el-table-column prop="programName" :label="$t('colProgramName')" min-width="180" />
            <el-table-column prop="countLimit" :label="$t('colCountLimit')" width="100" />
            <el-table-column prop="sumLimit" :label="$t('colServiceSumLimit')" width="130" />
            <el-table-column prop="franchise" :label="$t('deductible')" width="90" />
            <el-table-column prop="limitScopeName" :label="$t('colLimitScopeName')" min-width="150" />
          </el-table>
        </article>
      </section>

      <!-- Qruplar -->
      <section
        v-if="showProductGroupsSection"
        class="policy-detail-section"
        :class="{ 'policy-detail-section--last': !showProductExceptionsSection }"
      >
        <h2 class="policy-detail-section__title">{{ $t("groups") }}</h2>
        <article class="hezabat-card">
          <el-table :data="detail.groups || []" class="hezabat-table" stripe>
            <template #empty>
              <span class="policy-detail-table-empty">{{ $t("policyDetailTableEmpty") }}</span>
            </template>
            <el-table-column prop="groupName" :label="$t('group_1')" min-width="140" />
            <el-table-column prop="insSum" :label="$t('insSum_1')" width="120" />
            <el-table-column prop="premium" :label="$t('insSum')" width="110" />
            <el-table-column prop="sumScopeName" :label="$t('headerRules')" min-width="160" />
          </el-table>
        </article>
      </section>

      <!-- İstisnalar (son) -->
      <section
        v-if="showProductExceptionsSection"
        class="policy-detail-section policy-detail-section--last"
      >
        <h2 class="policy-detail-section__title">{{ $t("exceptions") }}</h2>
        <article class="hezabat-card">
          <el-table :data="detail.exceptions || []" class="hezabat-table" stripe>
            <template #empty>
              <span class="policy-detail-table-empty">{{ $t("policyDetailTableEmpty") }}</span>
            </template>
            <el-table-column prop="groupName" :label="$t('colGroup')" width="100" />
            <el-table-column prop="exceptName" :label="$t('colException')" min-width="180" />
            <el-table-column prop="caseCount" :label="$t('colCaseCount')" width="90" />
            <el-table-column prop="countLimitTypeName" :label="$t('colCountLimit_1')" width="140" />
            <el-table-column prop="caseSum" :label="$t('colCaseSum')" width="90" />
            <el-table-column prop="sumLimitTypeName" :label="$t('colSumLimit')" width="140" />
            <el-table-column prop="useTypeName" :label="$t('colUseType')" width="130" />
            <el-table-column prop="franchise" :label="$t('franchise')" width="80" />
            <el-table-column prop="notes" :label="$t('colNotes')" min-width="160" />
          </el-table>
        </article>
      </section>
    </template>
  </div>

  <el-dialog
    v-model="printVis"
    :close-on-click-modal="false"
    :destroy-on-close="true"
    custom-class="no-scroll-dialog policy-detail-print-dialog"
    width="1260px"
    top="5vh"
    style="max-height: 90vh"
    @closed="onPrintDialogClosed"
  >
    <div class="policy-detail-print-dialog__toolbar">
      <a
        v-if="printDownloadHref"
        class="policy-detail-print-dialog__open-link"
        :href="printDownloadHref"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ $t("linkDownloadPdf") || "Open" }}
      </a>
    </div>
    <p v-if="printError" class="policy-detail-print-dialog__error">
      {{ printError }}
    </p>
    <div class="policy-detail-print-dialog__frame-wrap">
      <iframe
        v-if="printVis && printSrc && !printError"
        :key="printSrc"
        :src="printSrc"
        class="policy-detail-print-dialog__frame"
        title="Print"
      />
    </div>
  </el-dialog>
</template>

<script>
import { loadPolicyDetail } from "./policyDetailApi.js";
import {
  buildDetailListRow,
  deleteAgentRequest,
  IS_POLICY_PROD,
  normalizeIsPolicyKind,
  isInvoiceNotIssuedStatus,
  shouldCheckNotificationStatus,
  parseNotificationStatusResponse,
  retryPolicyIssueAction,
  checkNotificationStatusAction,
  resendNotificationAction,
} from "./agentRequestsApi.js";
import Utils from "@/project/Source/utils.js";
import {
  buildPolicyPrintUrl,
  buildAgentRequestPrintUrl,
} from "@/project/Source/helper.js";

const MAIN_SCROLL_CLASS = "app-main-content--page-scroll";

export default {
  name: "PolicyDetailPage",
  data() {
    return {
      loading: false,
      deleting: false,
      detail: null,
      printVis: false,
      printSrc: null,
      printDownloadHref: "",
      printLoading: false,
      printError: "",
      printLoadTimer: null,
      retryingPolicyIssue: false,
      notificationChecking: false,
      notificationSending: false,
      notificationChecked: false,
      notificationSent: null,
    };
  },
  computed: {
    policyStatusContext() {
      const row = this.detailListRow || {};
      return {
        ...row,
        statusDisplay: this.detail?.statusDisplay || row.statusDisplay || row.statusName || "",
        statusName: row.statusName || this.detail?.statusDisplay || "",
        statusId: row.statusId ?? row.StatusId,
        status: row.status,
      };
    },
    policyActionReqId() {
      const fromDetail =
        this.detail?.reqId != null && String(this.detail.reqId).trim() !== ""
          ? String(this.detail.reqId).trim()
          : "";
      return fromDetail || this.productDeleteReqId || "";
    },
    policyActionPolicyId() {
      const id = this.detail?.medicalPolicy?.policyId;
      return id != null && String(id).trim() !== "" ? String(id).trim() : "";
    },
    hasPolicyActionId() {
      return Boolean(this.policyActionReqId || this.policyActionPolicyId);
    },
    showRetryPolicyIssueButton() {
      return (
        this.hasPolicyActionId &&
        isInvoiceNotIssuedStatus(this.policyStatusContext)
      );
    },
    showResendNotificationButton() {
      return (
        this.hasPolicyActionId &&
        this.notificationChecked &&
        this.notificationSent === false
      );
    },
    showInvoiceNotificationCallout() {
      return this.showRetryPolicyIssueButton || this.showResendNotificationButton;
    },
    invoiceNotificationCalloutText() {
      if (this.showRetryPolicyIssueButton) {
        return this.$t("policyDetailInvoiceNotIssuedHint");
      }
      return this.$t("policyDetailInvoiceNotificationHint");
    },
    canPrintPolicy() {
      if (!this.detail) return false;
      if (this.detail.detailMode === "policy") {
        const id = this.detail?.medicalPolicy?.policyId;
        return id != null && String(id).trim() !== "";
      }
      if (this.detail.detailMode === "product" || this.detail.detailMode === "agentRequest") {
        return Boolean(this.printReqId);
      }
      return false;
    },
    printReqId() {
      const fromDetail =
        this.detail?.reqId != null && String(this.detail.reqId).trim() !== ""
          ? String(this.detail.reqId).trim()
          : "";
      return fromDetail || this.productDeleteReqId || "";
    },
    /** Progress: print.progress-ims.com/print/0|15/{id}/inline */
    printIframeUrl() {
      if (this.detail?.detailMode === "policy") {
        const id = this.detail?.medicalPolicy?.policyId;
        return buildPolicyPrintUrl(id);
      }
      return buildAgentRequestPrintUrl(this.printReqId);
    },
    detailListRow() {
      return buildDetailListRow(this.policyId, history.state?.agentListRow ?? null, this.$route.query);
    },
    isProdPolicy() {
      const kind =
        this.detailListRow?.isPolicyKind ||
        normalizeIsPolicyKind(this.$route.query?.type ?? this.$route.query?.isPolicy);
      return kind === IS_POLICY_PROD;
    },
    showDeleteAgentRequest() {
      return this.isProdPolicy && Boolean(this.productDeleteReqId);
    },
    productDeleteReqId() {
      const row = this.detailListRow;
      const fromRow =
        row?.reqId != null && String(row.reqId).trim() !== "" ? String(row.reqId).trim() : "";
      const fromQuery =
        this.$route.query?.reqId != null && String(this.$route.query.reqId).trim() !== ""
          ? String(this.$route.query.reqId).trim()
          : "";
      return fromRow || fromQuery;
    },
    policyId() {
      return this.$route.params.id;
    },
    pageTitle() {
      const d = this.detail;
      if (!d) return this.policyId;
      if (d.detailMode === "product") {
        return d.productName || d.medicalPolicy?.policyFullNumber || this.policyId;
      }
      return d.medicalPolicy?.policyFullNumber || this.policyId;
    },
    showInsuredColumn() {
      if (this.detail?.detailMode === "policy") return true;
      if (this.detail?.detailMode === "product") {
        const ins = this.mp.insured;
        const ben = this.mp.beneficiary;
        const inv = this.mp.invoice;
        return Boolean(
          this.showInvoiceSection ||
            ins?.name ||
            ins?.address ||
            ins?.phone ||
            ben?.name ||
            ben?.address ||
            inv?.invoiceNumber ||
            inv?.invoiceLink
        );
      }
      return false;
    },
    showInvoiceSection() {
      const mode = this.detail?.detailMode;
      return mode === "policy" || (mode === "product" && this.detail?.policyFromAgent);
    },
    mp() {
      return this.detail?.medicalPolicy || {};
    },
    isProductDetail() {
      return this.detail?.detailMode === "product";
    },
    showInsuredCardsSection() {
      if (!this.detail) return false;
      if (this.detail.insuredCards?.length) return true;
      return this.isProductDetail && Boolean(this.detail.reqId);
    },
    showProductProgramsSection() {
      return this.isProductDetail || (this.detail?.programs?.length > 0);
    },
    showProductGroupsSection() {
      return this.isProductDetail || (this.detail?.groups?.length > 0);
    },
    showProductExceptionsSection() {
      return this.isProductDetail || (this.detail?.exceptions?.length > 0);
    },
    generalInfoRows() {
      const d = this.detail;
      const mp = this.mp;
      if (!d) return [];
      if (d.detailMode === "agentRequest") {
        return [
          { key: "policyNumber", label: this.$t("policy_number"), value: mp.policyFullNumber },
          { key: "product", label: this.$t("transactionProductLabel"), value: d.productName },
          { key: "status", label: this.$t("colStatus"), value: d.statusDisplay },
          { key: "endDate", label: this.$t("columnEndDate_1"), value: mp.endDateStr },
        ];
      }
      if (d.detailMode === "product") {
        const p = d.productRow || {};
        if (d.policyFromAgent) {
          const rows = [
            { key: "policyId", label: this.$t("claimId"), value: mp.policyId },
            {
              key: "dateOffice",
              label: this.$t("date_office"),
              value: [mp.policyDateStr, mp.branchName].filter(Boolean).join(" / "),
            },
            { key: "policyNumber", label: this.$t("policy_number"), value: mp.policyFullNumber },
            { key: "startDate", label: this.$t("startDate"), value: mp.startDateStr },
            { key: "endDate", label: this.$t("columnEndDate_1"), value: mp.endDateStr },
            ...(mp.cancelDateStr
              ? [{ key: "cancelDate", label: this.$t("cancellation_date"), value: mp.cancelDateStr }]
              : []),
            ...(mp.cancelTypeName
              ? [{ key: "cancelType", label: this.$t("cancellation_type"), value: mp.cancelTypeName }]
              : []),
            { key: "currency", label: this.$t("currency"), value: mp.currency },
            { key: "adminCharge", label: this.$t("administrative_expenses"), value: d.adminCharge },
            { key: "limitPremium", label: this.$t("insurance_payment_date"), value: d.limitPremium },
            { key: "seller", label: this.$t("seller"), value: d.sellerLogin },
          ];
          if (p.paymentCondition) {
            rows.push({
              key: "payment",
              label: this.$t("paymentCondition"),
              value: p.paymentCondition,
            });
          }
          if (p.eligibility) {
            rows.push({ key: "eligibility", label: this.$t("eligibility"), value: p.eligibility });
          }
          return rows;
        }
        return [
          { key: "productId", label: this.$t("transactionProductLabel"), value: p.productId ?? mp.policyId },
          { key: "name", label: this.$t("policy_number"), value: mp.policyFullNumber },
          { key: "adminCharge", label: this.$t("administrative_expenses"), value: d.adminCharge },
          { key: "startDate", label: this.$t("startDate"), value: mp.startDateStr },
          { key: "endDate", label: this.$t("columnEndDate_1"), value: mp.endDateStr },
          { key: "currency", label: this.$t("currency"), value: mp.currency },
          { key: "payment", label: this.$t("paymentCondition"), value: p.paymentCondition },
          { key: "eligibility", label: this.$t("eligibility"), value: p.eligibility },
        ];
      }
      return [
        { key: "policyId", label: this.$t("claimId"), value: mp.policyId },
        {
          key: "dateOffice",
          label: this.$t("date_office"),
          value: [mp.policyDateStr, mp.branchName].filter(Boolean).join(" / "),
        },
        { key: "policyNumber", label: this.$t("policy_number"), value: mp.policyFullNumber },
        { key: "startDate", label: this.$t("startDate"), value: mp.startDateStr },
        { key: "endDate", label: this.$t("columnEndDate_1"), value: mp.endDateStr },
        ...(mp.cancelDateStr
          ? [{ key: "cancelDate", label: this.$t("cancellation_date"), value: mp.cancelDateStr }]
          : []),
        ...(mp.cancelTypeName
          ? [{ key: "cancelType", label: this.$t("cancellation_type"), value: mp.cancelTypeName }]
          : []),
        { key: "currency", label: this.$t("currency"), value: mp.currency },
        { key: "adminCharge", label: this.$t("administrative_expenses"), value: d.adminCharge },
        { key: "limitPremium", label: this.$t("insurance_payment_date"), value: d.limitPremium },
        { key: "seller", label: this.$t("seller"), value: d.sellerLogin },
      ];
    },
    insuredRows() {
      const ins = this.mp.insured;
      if (!ins) return [];
      return [
        { key: "name", label: this.$t("beneficiary_full_name"), value: ins.name },
        { key: "address", label: this.$t("address"), value: ins.address },
        { key: "phone", label: this.$t("colPhone"), value: ins.phone },
      ];
    },
    beneficiaryRows() {
      const b = this.mp.beneficiary;
      if (!b) return [];
      return [
        { key: "name", label: this.$t("beneficiary_full_name"), value: b.name },
        { key: "address", label: this.$t("address"), value: b.address },
      ];
    },
    invoiceRows() {
      const inv = this.mp.invoice || {};
      return [
        {
          key: "number",
          label: this.$t("policyDetailInvoiceNumber"),
          value: inv.invoiceNumber,
          isLink: false,
        },
        {
          key: "link",
          label: this.$t("policyDetailInvoiceLink"),
          value: inv.invoiceLink,
          isLink: true,
        },
      ];
    },
  },
  watch: {
    "$route.fullPath"() {
      this.loadDetail();
    },
  },
  mounted() {
    this.enableMainScroll();
    this.loadDetail();
  },
  beforeUnmount() {
    this.disableMainScroll();
  },
  methods: {
    enableMainScroll() {
      document.querySelector(".app-main-content")?.classList.add(MAIN_SCROLL_CLASS);
    },
    disableMainScroll() {
      document.querySelector(".app-main-content")?.classList.remove(MAIN_SCROLL_CLASS);
    },
    async loadDetail() {
      this.loading = true;
      this.detail = null;
      this.resetNotificationState();
      try {
        const listRow = history.state?.agentListRow ?? null;
        this.detail = await loadPolicyDetail(this.policyId, {
          listRow,
          routeQuery: this.$route.query,
        });
        await this.afterDetailLoaded();
      } catch (err) {
        console.error("[PolicyDetail]", err?.response?.data ?? err);
        const msg =
          Utils.formatApiErrors(err?.response?.data?.errors) ||
          Utils.FormatErrorWithCorrelationId(err) ||
          (err?.message && err.message !== "Product not found" ? err.message : "");
        if (msg && err?.config?.silent) {
          Utils.Alert(msg);
        } else if (msg && !err?.response && !err?.config) {
          Utils.Alert(msg);
        }
        this.detail = null;
      } finally {
        this.loading = false;
      }
    },
    async onPrintPolicy() {
      if (this.printLoading) return;
      const url = this.printIframeUrl;
      console.log("[Print] iframe", {
        href: window.location.href,
        detailMode: this.detail?.detailMode,
        url,
      });
      if (!url) {
        Utils.Alert(this.$t("policyDetailPdfUnavailable"));
        return;
      }
      this.printLoading = true;
      try {
        this.clearPrintLoadTimer();
        this.printError = "";
        this.printDownloadHref = url;
        this.printSrc = url;
        this.printVis = true;
      } finally {
        this.printLoading = false;
      }
    },
    clearPrintLoadTimer() {
      if (this.printLoadTimer) {
        clearTimeout(this.printLoadTimer);
        this.printLoadTimer = null;
      }
    },
    onPrintDialogClosed() {
      this.clearPrintLoadTimer();
      this.printSrc = null;
      this.printDownloadHref = "";
      this.printLoading = false;
      this.printError = "";
    },
    resetNotificationState() {
      this.retryingPolicyIssue = false;
      this.notificationChecking = false;
      this.notificationSending = false;
      this.notificationChecked = false;
      this.notificationSent = null;
    },
    policyActionIds() {
      return {
        reqId: this.policyActionReqId,
        policyId: this.policyActionPolicyId,
      };
    },
    async afterDetailLoaded() {
      if (!this.detail || !this.hasPolicyActionId) return;
      if (isInvoiceNotIssuedStatus(this.policyStatusContext)) return;
      if (!shouldCheckNotificationStatus(this.policyStatusContext)) return;
      await this.fetchNotificationStatus();
    },
    async fetchNotificationStatus() {
      if (!this.hasPolicyActionId || this.notificationChecking) return;
      this.notificationChecking = true;
      try {
        const response = await checkNotificationStatusAction(this.policyActionIds());
        const parsed = parseNotificationStatusResponse(response);
        this.notificationChecked = parsed.checked;
        this.notificationSent = parsed.sent;
      } catch (err) {
        this.notificationChecked = true;
        this.notificationSent = false;
      } finally {
        this.notificationChecking = false;
      }
    },
    async onRetryPolicyIssue() {
      if (!this.showRetryPolicyIssueButton || this.retryingPolicyIssue) return;
      this.retryingPolicyIssue = true;
      try {
        await retryPolicyIssueAction(this.policyActionIds());
        Utils.Alert(this.$t("policyDetailRetryPolicyIssueSuccess"));
        await this.loadDetail();
      } catch (err) {
        const msg =
          Utils.formatApiErrors(err?.response?.data?.errors) ||
          Utils.FormatErrorWithCorrelationId(err);
        if (msg) Utils.Alert(msg);
      } finally {
        this.retryingPolicyIssue = false;
      }
    },
    async onResendNotification() {
      if (!this.showResendNotificationButton || this.notificationSending) return;
      this.notificationSending = true;
      try {
        await resendNotificationAction(this.policyActionIds());
        Utils.Alert(this.$t("policyDetailResendNotificationSuccess"));
        await this.fetchNotificationStatus();
      } catch (err) {
        const msg =
          Utils.formatApiErrors(err?.response?.data?.errors) ||
          Utils.FormatErrorWithCorrelationId(err);
        if (msg) Utils.Alert(msg);
      } finally {
        this.notificationSending = false;
      }
    },
    async onDeleteAgentRequest() {
      const reqId = this.productDeleteReqId;
      if (!reqId) return;

      const label = this.pageTitle || reqId;
      const result = await Utils.DeleteDialog(
        this.$t("policyDetailConfirmDelete", { name: label })
      );
      if (!result?.isConfirmed) return;

      this.deleting = true;
      try {
        await deleteAgentRequest(reqId);
        this.$router.push("/policies");
      } catch (err) {
        console.error("[PolicyDetail] AgentRequestDelete:", err?.response?.data ?? err);
        const msg =
          Utils.formatApiErrors(err?.response?.data?.errors) ||
          Utils.FormatErrorWithCorrelationId(err);
        if (msg) Utils.Alert(msg);
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style src="@/assets/scss/hezabat.scss" lang="scss"></style>
<style src="@/assets/scss/policy-detail.scss" lang="scss"></style>
